import { useMemo } from "react";
import { useParams, Link, Navigate } from "react-router-dom";
import { motion } from "framer-motion";
import * as Lucide from "lucide-react";
import Picture from "../components/Picture";
import BackLink from "../components/BackLink";
import SectionLabel from "../components/SectionLabel";
import SEO from "../components/SEO";
import CTABand from "../components/sections/CTABand";
import { BRANDS, BRAND_BY_SLUG } from "../data/brands";
import { SERVICES } from "../data/content";

const SERVICE_BY_SLUG = Object.fromEntries(SERVICES.map((s) => [s.slug, s]));

export default function BrandDetail() {
    const { slug } = useParams();
    const brand = BRAND_BY_SLUG[slug];

    // Stable alphabetical index for the eyebrow ("01" ... "12")
    const alphaIndex = useMemo(() => {
        const sorted = [...BRANDS].sort((a, b) => a.name.localeCompare(b.name));
        return sorted.findIndex((b) => b.slug === slug);
    }, [slug]);

    // Other brands strip — 6 brands that aren't the current one.
    const others = useMemo(() => {
        return BRANDS.filter((b) => b.slug !== slug).slice(0, 6);
    }, [slug]);

    if (!brand) {
        return <Navigate to="/brands" replace />;
    }

    const eyebrowNumber = String(alphaIndex + 1).padStart(2, "0");
    const services = brand.pairsWith
        .map((s) => SERVICE_BY_SLUG[s])
        .filter(Boolean);

    return (
        <div data-testid={`brand-detail-${brand.slug}`}>
            <SEO
                title={`${brand.name} Certified Integrator — Luxury Home Technology`}
                description={`${brand.tagline} ${brand.specialty} — specified by ACT in luxury residences.`}
                canonical={`https://actluxe.net/brands/${brand.slug}`}
                ogImage={brand.hero}
            />

            {/* Hero */}
            <section className="relative h-[70vh] min-h-[520px] w-full overflow-hidden bg-[#0B0B0C]">
                <Picture
                    slug={brand.hero}
                    alt={`${brand.name} — ${brand.specialty}`}
                    priority
                    sizes="100vw"
                    className="absolute inset-0 h-full w-full object-cover animate-ken-burns"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#0B0B0C] via-[#0B0B0C]/55 to-[#0B0B0C]/30" />

                <div className="relative h-full flex items-end pb-14 md:pb-20">
                    <div className="shell w-full">
                        <motion.div
                            suppressHydrationWarning
                            initial={{ opacity: 0, y: 16 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{
                                duration: 0.9,
                                ease: [0.22, 1, 0.36, 1],
                            }}
                            className="max-w-4xl"
                        >
                            <span className="eyebrow">
                                Trusted Partner · {eyebrowNumber}
                            </span>
                            <h1 className="mt-6 font-serif text-5xl md:text-6xl lg:text-7xl text-[#F3EFE7] leading-[1.04]">
                                {brand.name}
                            </h1>
                            <p className="mt-5 text-lg md:text-xl text-[#C9A86A] font-serif italic max-w-3xl leading-[1.55]">
                                {brand.tagline}
                            </p>
                        </motion.div>
                    </div>
                </div>
            </section>

            {/* Return-to-parent breadcrumb — sits under the hero on all deep
                pages for consistent up-hierarchy navigation. */}
            <div className="shell pt-6 md:pt-8 pb-2">
                <BackLink to="/brands" label="All brands" />
            </div>

            {/* Intro */}
            <section className="section-pad bg-[#0B0B0C] overflow-hidden">
                <div className="shell grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-20">
                    <div className="lg:col-span-4">
                        <SectionLabel number="—" label="Our Standard" />
                        <p className="mt-8 text-[10.5px] tracking-[0.32em] uppercase text-[#9A958A]">
                            {brand.specialty}
                        </p>
                    </div>
                    <div className="lg:col-span-8 space-y-7 text-[#F3EFE7] text-lg leading-[1.85]">
                        {brand.intro.map((para) => (
                            <p key={para.slice(0, 50)}>{para}</p>
                        ))}
                    </div>
                </div>
            </section>

            {/* Featured Products */}
            <section className="bg-[#15161A] border-y border-[rgba(201,168,106,0.12)] overflow-hidden">
                <div className="shell py-20 md:py-28">
                    <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-20 mb-12 md:mb-16">
                        <div className="lg:col-span-7">
                            <SectionLabel
                                number="01"
                                label="Featured Products · The Top Four"
                            />
                            <h2 className="mt-8 font-serif text-3xl md:text-4xl lg:text-5xl leading-[1.1]">
                                The pieces we{" "}
                                <span className="italic text-[#C9A86A]">
                                    routinely specify.
                                </span>
                            </h2>
                        </div>
                        <p className="lg:col-span-5 text-[#9A958A] text-[15px] leading-[1.85] lg:pt-12">
                            A curated selection of {brand.name}&rsquo;s
                            flagship range — the products our installers reach
                            for in nearly every residence at this tier.
                        </p>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-5 md:gap-6">
                        {brand.products.map((product, idx) => {
                            return (
                                <motion.article
                                    key={product.name}
                                    suppressHydrationWarning
                                    initial={{ opacity: 0, y: 14 }}
                                    whileInView={{ opacity: 1, y: 0 }}
                                    viewport={{ once: true, amount: 0.15 }}
                                    transition={{
                                        duration: 0.7,
                                        delay: (idx % 2) * 0.08,
                                        ease: [0.22, 1, 0.36, 1],
                                    }}
                                    className="border border-[rgba(201,168,106,0.22)] bg-[#0B0B0C] flex flex-col"
                                    data-testid={`product-card-${brand.slug}-${idx}`}
                                >
                                    {/* Product image — 4:3 frame keeps landscape products (e.g. SmartAmp 2.21:1) from
                                        floating in excess vertical space and tightens the overall card height so the
                                        category/name/description block reads as proportional. Dark studio backdrops
                                        on the product photos blend into the card surface; the gold hairline frames
                                        the product zone. */}
                                    <div className="relative aspect-[4/3] overflow-hidden bg-[#0B0B0C]">
                                        <Picture
                                            slug={product.image}
                                            alt={product.name}
                                            sizes="(max-width: 768px) 92vw, 42vw"
                                            className="absolute inset-0 h-full w-full object-contain"
                                        />
                                        <span
                                            aria-hidden="true"
                                            className="absolute inset-0 border border-[rgba(201,168,106,0.2)] pointer-events-none"
                                        />
                                    </div>

                                    <div className="p-7 md:p-8 flex-1 flex flex-col">
                                        <span className="text-[10px] tracking-[0.32em] uppercase text-[#C9A86A]">
                                            {product.category}
                                        </span>
                                        <h3 className="mt-3 font-serif text-2xl md:text-[26px] text-[#F3EFE7] leading-[1.18]">
                                            {product.name}
                                        </h3>
                                        <p className="mt-4 text-[#9A958A] text-[14.5px] leading-[1.75]">
                                            {product.description}
                                        </p>

                                        <div className="mt-6 hairline-solid" />

                                        <div className="mt-6 flex gap-4 items-start">
                                            <span
                                                aria-hidden="true"
                                                className="block w-px self-stretch bg-[#C9A86A] mt-0.5"
                                            />
                                            <div className="flex-1">
                                                <span className="text-[9.5px] tracking-[0.32em] uppercase text-[#C9A86A]">
                                                    Why this matters
                                                </span>
                                                <p className="mt-2 text-[#F3EFE7] text-[14.5px] leading-[1.75] italic font-serif">
                                                    {product.why}
                                                </p>
                                            </div>
                                        </div>
                                    </div>
                                </motion.article>
                            );
                        })}
                    </div>
                </div>
            </section>

            {/* Integration Benefits */}
            <section className="section-pad bg-[#0B0B0C] overflow-hidden">
                <div className="shell">
                    <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-20 mb-12">
                        <div className="lg:col-span-5">
                            <SectionLabel
                                number="02"
                                label="What This Means For Your Residence"
                            />
                            <h2 className="mt-8 font-serif text-3xl md:text-4xl lg:text-5xl leading-[1.1]">
                                Why we deploy{" "}
                                <span className="italic text-[#C9A86A]">
                                    {brand.name}.
                                </span>
                            </h2>
                        </div>
                    </div>

                    <ol className="grid grid-cols-1 md:grid-cols-3 gap-8 md:gap-12">
                        {brand.benefits.map((benefit, idx) => (
                            <li
                                key={benefit.slice(0, 40)}
                                className="flex gap-6"
                                data-testid={`benefit-${brand.slug}-${idx}`}
                            >
                                <span
                                    aria-hidden="true"
                                    className="font-serif text-4xl md:text-5xl text-[#C9A86A] leading-none shrink-0"
                                >
                                    {String(idx + 1).padStart(2, "0")}
                                </span>
                                <p className="text-[#F3EFE7] text-[15.5px] leading-[1.8] font-light pt-1">
                                    {benefit}
                                </p>
                            </li>
                        ))}
                    </ol>
                </div>
            </section>

            {/* Pairs With */}
            {services.length > 0 && (
                <section className="bg-[#15161A] border-t border-[rgba(201,168,106,0.12)] overflow-hidden">
                    <div className="shell py-16 md:py-20">
                        <SectionLabel number="03" label="Services" />
                        <h2 className="mt-6 font-serif text-2xl md:text-3xl text-[#F3EFE7] mb-8">
                            Pairs with
                        </h2>
                        <div className="flex flex-wrap gap-3 md:gap-4">
                            {services.map((s) => (
                                <Link
                                    key={s.slug}
                                    to={`/services/${s.slug}`}
                                    className="inline-flex items-center gap-2 px-5 py-3 border border-[#C9A86A]/50 text-[#C9A86A] text-[12px] tracking-[0.22em] uppercase transition-all duration-400 ease-out hover:bg-[#C9A86A] hover:text-[#0B0B0C] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#C9A86A] focus-visible:ring-offset-2 focus-visible:ring-offset-[#15161A]"
                                    data-testid={`pairs-with-${s.slug}`}
                                >
                                    {s.name}
                                    <Lucide.ArrowUpRight
                                        size={13}
                                        strokeWidth={1.4}
                                    />
                                </Link>
                            ))}
                        </div>
                    </div>
                </section>
            )}

            {/* Brand-specific CTA band */}
            <section className="relative bg-[#0B0B0C] border-t border-[rgba(201,168,106,0.15)] overflow-hidden">
                <div
                    aria-hidden="true"
                    className="absolute inset-0 bg-gradient-to-r from-transparent via-[rgba(201,168,106,0.06)] to-transparent"
                />
                <div className="relative shell py-20 md:py-24 text-center">
                    <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl text-[#F3EFE7] leading-[1.08] max-w-3xl mx-auto">
                        Specify{" "}
                        <span className="italic text-[#C9A86A]">
                            {brand.name}
                        </span>{" "}
                        in your residence.
                    </h2>
                    <p className="mt-6 text-[#9A958A] text-base md:text-lg leading-[1.85] max-w-xl mx-auto">
                        A private conversation begins with a single, considered
                        message.
                    </p>
                    <div className="mt-10">
                        <Link
                            to="/contact"
                            className="btn-gold inline-flex"
                            data-testid={`brand-cta-${brand.slug}`}
                        >
                            Book a Consultation
                            <Lucide.ArrowUpRight
                                size={14}
                                strokeWidth={1.4}
                            />
                        </Link>
                    </div>
                </div>
            </section>

            {/* Other partners strip */}
            <section className="bg-[#0B0B0C] border-t border-[rgba(201,168,106,0.1)] overflow-hidden">
                <div className="shell py-14 md:py-20">
                    <SectionLabel
                        number="—"
                        label="Other Trusted Partners"
                    />
                    <div className="mt-8 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4 md:gap-5">
                        {others.map((b) => (
                            <Link
                                key={b.slug}
                                to={`/brands/${b.slug}`}
                                className="group block border border-[rgba(201,168,106,0.15)] bg-[#15161A]/50 hover:border-[rgba(201,168,106,0.5)] transition-all duration-500 ease-out focus:outline-none focus-visible:ring-2 focus-visible:ring-[#C9A86A]"
                                data-testid={`other-brand-${b.slug}`}
                            >
                                <div className="relative aspect-[16/10] overflow-hidden bg-[#1C1D22]">
                                    <Picture
                                        slug={b.hero}
                                        alt={b.name}
                                        sizes="(max-width: 768px) 50vw, 16vw"
                                        className="absolute inset-0 h-full w-full object-cover transition-transform duration-700 ease-out group-hover:scale-105"
                                    />
                                    <div className="absolute inset-0 bg-gradient-to-t from-[#0B0B0C]/80 via-transparent to-transparent" />
                                </div>
                                <div className="px-3 py-3 text-center">
                                    <span className="font-serif text-sm md:text-base text-[#F3EFE7] group-hover:text-[#D9BC80] transition-colors">
                                        {b.name}
                                    </span>
                                </div>
                            </Link>
                        ))}
                    </div>
                </div>
            </section>

            <CTABand />
        </div>
    );
}
